import React from 'react';

// Renders a clickable <th> with an arrow indicator that reflects the
// hook's current sortKey/sortDirection. Plain inline styles + no UI
// library on purpose — the codebase otherwise uses bare <table>
// markup and we don't want to introduce a styling system just for
// sort headers.
//
// Accessibility:
//   - aria-sort reflects the column's current state for screen readers.
//   - The inner <button> is keyboard-focusable; clicking a <th>
//     directly would not be reachable via Tab.
//   - The arrow character is wrapped with aria-hidden so screen
//     readers rely on aria-sort instead of decorative glyphs.

const ARROW_NONE = '—';
const ARROW_ASC = '▲';   // ▲
const ARROW_DESC = '▼';  // ▼

function SortableHeader({
  sortKey,
  label,
  activeKey,
  direction,
  onSort,
  children,
  className,
  style,
  thProps,
  align,
}) {
  const isActive = activeKey === sortKey;
  const ariaSort = !isActive
    ? 'none'
    : direction === 'asc'
      ? 'ascending'
      : 'descending';
  const arrow = !isActive
    ? ARROW_NONE
    : direction === 'asc'
      ? ARROW_ASC
      : ARROW_DESC;

  // Merge consumer's <th> style with our defaults — text-align
  // matters here because the existing tables sometimes set right
  // alignment for numeric columns, and we mustn't override it.
  const thStyle = {
    cursor: 'pointer',
    userSelect: 'none',
    ...(style || {}),
  };

  // Inner button aligns to the th's text-align. Without this, a
  // right-aligned numeric column would still show "Label ▼" pinned to
  // the left of the cell.
  const justify = align === 'right'
    ? 'flex-end'
    : align === 'center'
      ? 'center'
      : 'flex-start';

  return (
    <th
      {...(thProps || {})}
      aria-sort={ariaSort}
      className={className}
      style={thStyle}
      onClick={() => onSort(sortKey)}
    >
      <button
        type="button"
        // The whole <th> is clickable, so the button is mostly there
        // for keyboard accessibility. Don't double-fire the parent's
        // onClick when keyboard-activating the button.
        onClick={(e) => { e.stopPropagation(); onSort(sortKey); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: justify,
          gap: 4,
          width: '100%',
          padding: 0,
          margin: 0,
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          font: 'inherit',
          textAlign: 'inherit',
          cursor: 'pointer',
        }}
      >
        <span>{children ?? label}</span>
        <span
          aria-hidden="true"
          style={{
            fontSize: '0.7em',
            opacity: isActive ? 1 : 0.35,
            minWidth: '0.7em',
          }}
        >
          {arrow}
        </span>
      </button>
    </th>
  );
}

export default SortableHeader;
